import { resolveContentType } from '@atlas/types'
import type { ConcernState } from '@atlas/ui-components'
import { apiErrorMessage } from '@/lib/api-error'
import { createClientId } from '@/lib/client-id'
import { logger } from '@/lib/logger'
import type { ClassifyTuple } from '@/services/classification.service'
import { type FileDTO, fileService } from '@/services/file.service'
import { scheduleConfirmCatchUp } from './confirm-catch-up'
import { type ConcernOutcome, runDetached, uploadCallbacks } from './upload-callbacks'
import { closeCancellation, openCancellation, requestCancellation, resetCancellations } from './upload-cancellation'
import type { UploadItem } from './upload-item.types'
import { rejectionReason } from './upload-precheck'
import { recoveryFrom, UploadRecoveryAction } from './upload-recovery-outcome'
import {
	clearRefreshSequences,
	markConcernPending as markConcernPendingOn,
	type RefreshHost,
	refreshItemFromServer as refreshItemFromServerOn,
	retryConcern as retryConcernOn,
	toConcerns,
	type UploadFileGateway,
} from './upload-server-refresh'
import { deriveStatus, isConfirmedStatus, isTerminalStatus, UploadItemStatus } from './upload-status'
import { summarize, type UploadSummary } from './upload-summary'
import { uploaderService } from './uploader/uploader-service'

const log = logger.child('upload-store')

export { UPLOAD_RECOVERY_LABELS, type UploadRecovery, UploadRecoveryAction } from './upload-recovery'
export type { UploadFileGateway } from './upload-server-refresh'
export {
	deriveStatus,
	isCancellableStatus,
	isConfirmedStatus,
	isFinishedWithErrors,
	isPreUploadFailure,
	isSettled,
	isTerminalStatus,
	UploadItemStatus,
} from './upload-status'
// Re-exported so the store stays the one import site for upload state; the
// derivation itself lives in `upload-status.ts` because it is pure.
export { summarize, type UploadSummary } from './upload-summary'
export type { ConcernOutcome, UploadItem }

export interface EnqueueOptions {
	/**
	 * Classification tuples forwarded verbatim into `confirmUpload`. The manager
	 * never calls the classification API itself — the server applies these as a
	 * `classification` concern, so a retry reuses them without resubmission.
	 */
	classify?: ClassifyTuple[]
	/**
	 * Builds the tuples once the server-assigned file id is known. Every tuple
	 * names the file it classifies, so a flow that classifies its own upload
	 * cannot supply a static `classify` array — it has to defer to this.
	 * Ignored when `classify` is given.
	 */
	buildClassify?: (fileId: string) => ClassifyTuple[]
	/**
	 * Invoked once if the file never completes: either a pre-check rejection
	 * (wrong type or too large, so nothing is ever sent) or a mid-flight failure.
	 * Both are reported so a caller awaiting the upload always gets an answer.
	 */
	onFailed?: (message: string) => void
	/**
	 * Invoked once when the server confirms the upload, before any concern has
	 * finished. Distinct from {@link onSettled}: Upload & Classify stages a file
	 * for classification as soon as it exists, which is a confirm-time concern,
	 * not a processing-time one.
	 */
	onConfirmed?: (file: FileDTO) => void
	/**
	 * Invoked exactly once, when this item's computed status first reaches
	 * `SETTLED` — every concern reported and every one of them completed.
	 */
	onSettled?: (file: FileDTO) => void | Promise<void>
	/**
	 * Invoked exactly once when the named concern reaches a terminal state —
	 * `completed` OR `failed` — and also when the server reports the upload
	 * confirmed without ever creating that concern's row.
	 *
	 * Distinct from {@link onSettled}: a work-order follow-up that reads back a
	 * file's classification depends on the `classification` concern and nothing
	 * else, so making it wait for an unrelated thumbnail render would delay the
	 * activity feed for no reason.
	 *
	 * It fires on failure and on absence, not only on success, because the
	 * caller's follow-up work is what makes the upload *visible* to its work
	 * order. Firing only on success meant a failed concern silently stranded the
	 * file — stored, but attached to nothing, with no error shown.
	 */
	onConcernSettled?: {
		concernId: string
		run: (outcome: ConcernOutcome) => void | Promise<void>
	}
	/** Tags the item with the surface that enqueued it — see {@link UploadItem.owner}. */
	owner?: string
	/**
	 * Store the object in the public bucket, whose URLs never expire, instead of
	 * the private one. Defaults to private, so every existing caller is unchanged.
	 *
	 * Only for content whose URL has to outlive a presigned window — an article's
	 * inline images, which live inside stored HTML and would otherwise render on
	 * the day they were written and break afterwards. A public object is readable
	 * by anyone holding its URL, so this is a deliberate per-surface choice and
	 * never a default.
	 */
	isPublic?: boolean
	/** `false` creates the File inactive: a work order's draft design file. Defaults to active. */
	isActive?: boolean
}

/**
 * Cap on parallel PUTs so a large drop doesn't open dozens of connections.
 * Not a limit on how many files may be selected — the queue drains any number
 * of them this many at a time.
 */
const MAX_CONCURRENT_UPLOADS = 5

/** Progress percent once transfer to the server has fully completed. */
const UPLOAD_PROGRESS_COMPLETE = 100

// ─── Module state ────────────────────────────────────────────────────────────

interface QueueEntry {
	id: string
	file: File
	/** Resolved once in `enqueue()`; carried through so `performUpload` never recomputes it. */
	contentType: string
	options: EnqueueOptions
	/** Set only by `Start over`: a fresh attempt against a new File, never a continuation. */
	restart?: boolean
}

let items: UploadItem[] = []
let queue: QueueEntry[] = []
/**
 * The entry each item was enqueued with, kept for as long as the item exists.
 *
 * A recovery action re-runs the SAME upload — same file, same classification,
 * same callbacks — and the queue entry is the only place that identity lives
 * once the entry has been shifted off the queue.
 */
const entries = new Map<string, QueueEntry>()
let activeCount = 0
const listeners = new Set<() => void>()

function emit(): void {
	for (const listener of listeners) listener()
	syncUnloadGuard()
}

export function subscribe(listener: () => void): () => void {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

export function getItems(): UploadItem[] {
	return items
}

/** Applies the status an item's own concern map implies — see {@link deriveStatus}. */
function withDerivedStatus(item: UploadItem): UploadItem {
	const derived = deriveStatus(item)
	return derived === item.status ? item : { ...item, status: derived }
}

/**
 * Replaces an item, recomputing its derived status from the result. Rendering
 * order is preserved by mapping in place — a tile must never jump position
 * because its status changed.
 */
function patchItem(id: string, patch: Partial<UploadItem>): void {
	items = items.map((item) => {
		if (item.id !== id) return item
		return withDerivedStatus({ ...item, ...patch })
	})
	const item = items.find((candidate) => candidate.id === id)
	if (item) {
		const isConfirmed = isConfirmedStatus(item.status)
		uploadCallbacks.fireConcern(id, item.concerns, isConfirmed)
		uploadCallbacks.fireSettled(id, item.status === UploadItemStatus.SETTLED)
	}
	emit()
}

export function replaceConcerns(id: string, concerns: Record<string, ConcernState>): void {
	patchItem(id, { concerns })
}

/** Records which attribute axis codes failed to save for an item — see {@link UploadItem.attributeFailures}. */
export function setAttributeFailures(id: string, axisCodes: string[]): void {
	patchItem(id, { attributeFailures: axisCodes })
}

/**
 * The store operations the server-refresh module works through. Declared once
 * here so the delegations below read as forwarding rather than as four separate
 * hand-offs.
 */
const refreshHost: RefreshHost = { items: getItems, patchItem }

/** Puts a single concern back to pending after a retry was accepted, leaving siblings untouched. */
export function markConcernPending(id: string, concernId: string): void {
	markConcernPendingOn(refreshHost, id, concernId)
}

/** See {@link refreshItemFromServerOn}. */
export function refreshItemFromServer(fileId: string, gateway: UploadFileGateway): Promise<void> {
	return refreshItemFromServerOn(refreshHost, fileId, gateway)
}

/** See {@link retryConcernOn}. */
export function retryConcern(id: string, concernId: string, gateway: UploadFileGateway): Promise<void> {
	return retryConcernOn(refreshHost, id, concernId, gateway)
}

export function removeItem(id: string): void {
	items = items.filter((item) => item.id !== id)
	uploadCallbacks.forget(id)
	entries.delete(id)
	emit()
}

/** Test-only: drop all state so one spec's queue cannot leak into the next. */
export function resetUploadStore(): void {
	items = []
	queue = []
	activeCount = 0
	entries.clear()
	resetCancellations()
	clearRefreshSequences()
	uploadCallbacks.clear()
	syncUnloadGuard()
}

// ─── Unload guard ────────────────────────────────────────────────────────────

const beforeUnload = (event: BeforeUnloadEvent): void => {
	event.preventDefault()
}

let guardAttached = false

/**
 * Warns before a reload that would abandon in-flight work. `SETTLED` already
 * encodes "every concern finished", so the terminal set is the whole check —
 * there is no separate in-flight-concern condition to test.
 */
function syncUnloadGuard(): void {
	if (typeof window === 'undefined') return
	const shouldGuard = items.some((item) => !isTerminalStatus(item.status))
	if (shouldGuard === guardAttached) return
	guardAttached = shouldGuard
	if (shouldGuard) window.addEventListener('beforeunload', beforeUnload)
	else window.removeEventListener('beforeunload', beforeUnload)
}

// ─── Upload pipeline ─────────────────────────────────────────────────────────

async function performUpload(entry: QueueEntry): Promise<FileDTO> {
	// upload-store owns and sets this status BEFORE calling the transport —
	// there is no uploader callback for it, because nothing transport-specific
	// has happened yet.
	patchItem(entry.id, { status: UploadItemStatus.REQUESTING_URL })

	const { isPublic, isActive, classify, buildClassify } = entry.options
	return uploaderService.execute(
		{
			file: entry.file,
			contentType: entry.contentType,
			isPublic,
			isActive,
			classify,
			buildClassify,
			signal: openCancellation(entry.id),
			...(entry.restart ? { restart: true } : {}),
		},
		{
			onFileCreated: (fileId) => patchItem(entry.id, { status: UploadItemStatus.UPLOADING, fileId }),
			onProgress: (percent) => patchItem(entry.id, { progress: percent }),
			onConfirming: () =>
				patchItem(entry.id, { status: UploadItemStatus.CONFIRMING, progress: UPLOAD_PROGRESS_COMPLETE }),
		},
	)
}

function pump(): void {
	while (activeCount < MAX_CONCURRENT_UPLOADS) {
		const entry = queue.shift()
		if (!entry) return
		activeCount += 1
		void performUpload(entry)
			.then((file) => {
				uploadCallbacks.rememberConfirmed(entry.id, file)
				// Deferred, not called inline: a synchronous throw from a consumer
				// (this is a React setState in Upload & Classify) would otherwise land
				// in the sibling .catch() below and mark a successfully-stored upload
				// as FAILED. The consumer's bug must not rewrite the upload's outcome.
				runDetached(() => entry.options.onConfirmed?.(file))
				// One patch carries both the confirmed status and the first concern
				// snapshot, so a file whose concerns all completed synchronously
				// settles immediately rather than rendering a spurious intermediate.
				log.debug('confirmed', {
					fileId: file.fileId,
					concerns: Object.fromEntries(Object.entries(file.processingResults ?? {}).map(([id, e]) => [id, e.status])),
				})
				patchItem(entry.id, {
					status: UploadItemStatus.CONFIRMED,
					progress: 100,
					fileId: file.fileId,
					concerns: toConcerns(file),
				})
				// Reconcile once, shortly after confirm, WITHOUT waiting for a push.
				//
				// Concerns often finish within a few hundred milliseconds of confirm —
				// faster than the realtime subscription for this file id can be
				// registered. MQTT does not replay, so those completions are missed
				// and, with the connection up, the poll fallback is off: the tile
				// would sit on "Classifying…" forever while the row reads `completed`.
				//
				// This is a single background GET /files/{id}; push remains the
				// primary path for everything after it.
				scheduleConfirmCatchUp(
					entry.id,
					file.fileId,
					(id) => items.find((candidate) => candidate.id === id),
					(fileId) => void refreshItemFromServer(fileId, fileService),
				)
			})
			.catch((error: unknown) => {
				const message = apiErrorMessage(error, 'Upload failed')
				// `recovery` is replaced on every attempt, never merged: an offer from
				// an earlier failure describes a session state that no longer holds.
				patchItem(entry.id, {
					status: UploadItemStatus.FAILED,
					error: message,
					recovery: recoveryFrom(error) ?? undefined,
				})
				entry.options.onFailed?.(message)
			})
			.finally(() => {
				closeCancellation(entry.id)
				activeCount -= 1
				pump()
			})
	}
}

/**
 * Runs one of the actions a failed item's recovery offer carries.
 *
 * Every one of them re-enters the SAME orchestration a first attempt uses —
 * the queue, its concurrency budget, the item's own status and progress — so
 * recovery introduces no second upload path. What differs between them is only
 * whether the server-side session is released first and whether the retry is a
 * continuation or a fresh attempt.
 */
export async function recover(id: string, action: UploadRecoveryAction): Promise<void> {
	const item = items.find((candidate) => candidate.id === id)
	const entry = entries.get(id)
	if (!item || !entry) return

	// Close stops client observation and nothing else: the server session and the
	// transport's own resume metadata are deliberately left alone.
	if (action === UploadRecoveryAction.CLOSE) return removeItem(id)

	if (action === UploadRecoveryAction.CANCEL || action === UploadRecoveryAction.DISCARD) {
		// The session has to actually be released before anything else happens: a
		// session still in flight blocks the File, so a fresh attempt started over
		// the top of it would only collide with it again.
		await item.recovery?.releaseSession?.()
		if (action === UploadRecoveryAction.CANCEL) return removeItem(id)
	}

	const restart = action === UploadRecoveryAction.START_OVER || action === UploadRecoveryAction.DISCARD
	requeue({ ...entry, ...(restart ? { restart: true } : {}) }, restart)
}

/**
 * Puts an item back through the queue in place.
 *
 * The item keeps its id and its position — a tile must not jump because the
 * user retried it — and a restart resets the progress and the File the previous
 * attempt had reached, because `Start over` is defined as a new attempt from
 * zero rather than a continuation.
 */
function requeue(entry: QueueEntry, restart: boolean): void {
	entries.set(entry.id, entry)
	patchItem(entry.id, {
		status: UploadItemStatus.QUEUED,
		error: null,
		recovery: undefined,
		...(restart ? { progress: 0, fileId: null, concerns: {} } : {}),
	})
	queue.push(entry)
	pump()
}

/**
 * Cancels one upload.
 *
 * An item still waiting in the queue is simply removed — nothing has been sent
 * for it. An in-flight one is cancelled through its `AbortSignal`: the
 * transport aborts its own requests and releases any server-side session it
 * created, and the rejection it produces reaches the existing failure path
 * unchanged.
 */
export function cancelUpload(id: string): void {
	if (requestCancellation(id)) return
	const queuedIndex = queue.findIndex((entry) => entry.id === id)
	if (queuedIndex === -1) return
	queue.splice(queuedIndex, 1)
	// Removed, not failed: nothing was ever sent, so there is nothing to show a
	// terminal status for — and removeItem is what actually drops the queue
	// entry, leaving no tile with no dismiss action behind.
	removeItem(id)
}

/** Queues one file. Rejected files are still tracked, so the user sees why. */
export function enqueue(file: File, options: EnqueueOptions = {}): string {
	const id = createClientId('upload')
	const reason = rejectionReason(file)
	// Resolved so the tile's type marker matches what is actually uploaded, rather
	// than reading FILE for every PSD on Safari — and reused for the transport
	// request below, so the two never risk disagreeing.
	const contentType = resolveContentType(file.name, file.type)

	items = [
		...items,
		{
			id,
			fileName: file.name,
			fileSize: file.size,
			contentType,
			status: reason ? UploadItemStatus.REJECTED : UploadItemStatus.QUEUED,
			progress: 0,
			error: reason,
			fileId: null,
			concerns: {},
			file,
			owner: options.owner,
		},
	]

	if (!reason) {
		uploadCallbacks.register(id, options)
		const entry: QueueEntry = { id, file, contentType, options }
		entries.set(id, entry)
		queue.push(entry)
	}

	emit()
	if (reason) {
		// A rejected file never enters the queue, so nothing downstream would ever
		// report it — a caller awaiting this upload would hang forever.
		options.onFailed?.(reason)
	} else {
		pump()
	}
	return id
}

/** Queues several files at once, preserving the order they were dropped in. */
export function enqueueAll(files: File[], options: EnqueueOptions = {}): string[] {
	return files.map((file) => enqueue(file, options))
}

/** {@link summarize} over the store's current items. */
export function getSummary(): UploadSummary {
	return summarize(items)
}
